import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';

import { ReactComponent as FreezeIcon } from '../../assets/icons/freeze-icon.svg';
import { ReactComponent as SizeChangeIcon } from '../../assets/icons/table-field-allow-icon.svg';
import CustomInputSwitch from '../custom-input-switch/CustomInputSwitch';

interface HideColumnItem {
  label: string;
  accessorKey: any;
  disableHideFunc: boolean;
}

interface SortableColumnItemProperties {
  id: string;
  field: HideColumnItem;
  isChecked: boolean;
  isFrozen: boolean;
  isBoundary: boolean;
  onToggle: (name: string) => void;
  onToggleFreeze: (key: string) => void;
}

/**
 * Sortable row item inside the column show/hide popup with a freeze button and draggable column label.
 * @param {SortableColumnItemProperties} properties - Component properties.
 * @returns {React.JSX.Element} The rendered sortable column row.
 */
function SortableColumnItem({
  id,
  field,
  isChecked,
  isFrozen,
  isBoundary,
  onToggle,
  onToggleFreeze,
}: SortableColumnItemProperties): React.JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`allow-field-item-row ${isDragging ? 'dragging' : ''} ${isFrozen ? 'frozen-row' : ''}`}>
      <button
        type="button"
        className={`freeze-button ${isFrozen ? 'active' : ''}`}
        title={isBoundary ? 'Unfreeze columns' : `Freeze columns up to ${field.label}`}
        onClick={(event) => {
          event.stopPropagation();
          onToggleFreeze(String(field.accessorKey));
        }}>
        <FreezeIcon />
      </button>
      <CustomInputSwitch
        value={isChecked}
        name={String(field.accessorKey)}
        onChangeValue={onToggle}
        fontSize="small"
      />
      {/* eslint-disable react/jsx-props-no-spreading */}
      <span
        className="column-drag-label"
        aria-label="Drag column"
        {...attributes}
        {...listeners}>
        {field.label}
      </span>
      {/* eslint-enable react/jsx-props-no-spreading */}
    </div>
  );
}

interface TableFieldAllowInterFace {
  labelForHideColumn: HideColumnItem[];
  hidedColumn: string[];
  setHidedColumn: React.Dispatch<React.SetStateAction<string[]>>;
  hoverLabel?: string;
  onColumnOrderChange?: (orderedKeys: string[]) => void;
  frozenColumn?: string | null;
  onFrozenColumnChange?: (columnKey: string | null) => void;
}

/**
 * Table Field Allow function component
 * @param {TableFieldAllowInterFace} properties - The component properties.
 * @returns {React.JSX.Element} TableFieldAllow component.
 */
function TableFieldAllow({
  labelForHideColumn,
  hidedColumn,
  setHidedColumn,
  hoverLabel,
  onColumnOrderChange,
  frozenColumn,
  onFrozenColumnChange,
}: TableFieldAllowInterFace): React.JSX.Element {
  const { t } = useTranslation('sidePanel');
  const label: string = '';
  const reference = useRef<HTMLDivElement>(null);
  const buttonReference = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const [fields, setFields] = useState<HideColumnItem[]>(labelForHideColumn);

  useEffect(() => {
    setFields(labelForHideColumn);
  }, [labelForHideColumn]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const displayFields = useMemo(
    () => fields.filter((eachLabel) => !eachLabel.disableHideFunc),
    [fields],
  );

  const sortableIds = useMemo(
    () => displayFields.map((field) => String(field.accessorKey)),
    [displayFields],
  );

  const frozenIndex = useMemo(() => {
    if (!frozenColumn) return -1;
    return displayFields.findIndex(
      (field) => String(field.accessorKey) === String(frozenColumn),
    );
  }, [displayFields, frozenColumn]);

  const isAnyColumnHidden =
    hidedColumn.length > 0 &&
    fields.some((column) => !column.disableHideFunc && hidedColumn.includes(column.accessorKey));

  /**
   * Function to update the dropdown position
   */
  const updatePosition = () => {
    if (!buttonReference.current) return;
    const rect = buttonReference.current.getBoundingClientRect();
    setStyle({
      top: rect.top + rect.height + 4,
      left: rect.left,
    });
  };

  /**
   * Toggle the dropdown modal
   */
  const toggleModal = () => {
    setOpen((previous) => !previous);
  };

  /**
   * Handle the close th drop down function
   * @param {MouseEvent} event - Mouse click event
   */
  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest('.table-column-show-hide-button')) {
      return;
    }

    if (reference.current && !reference.current.contains(target)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;

    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useEffect((): void => {
    document.addEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Function to Hide & Show all fields
   * @param {'single' | 'multi'} type - Single or multi update
   * @param {string} key - The column key or action (hideAll/showAll)
   */
  const hideAndShowFields = (type: 'single' | 'multi', key: string) => {
    if (type === 'multi') {
      if (key === 'hideAll') {
        setHidedColumn(
          fields
            .filter((eachLabel) => !eachLabel.disableHideFunc)
            .map((eachLabel) => eachLabel.accessorKey),
        );
      }

      if (key === 'showAll') {
        setHidedColumn([]);
      }
    } else {
      setHidedColumn((previous) => {
        if (previous.includes(key)) {
          return previous.filter((item) => item !== key);
        }
        return [...previous, key];
      });
    }
  };

  /**
   * Handle column drag end and notify parent of new order
   * @param {DragEndEvent} event - Drag end event
   */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = displayFields.findIndex(
      (item) => String(item.accessorKey) === String(active.id),
    );
    const newIndex = displayFields.findIndex(
      (item) => String(item.accessorKey) === String(over.id),
    );

    if (oldIndex !== -1 && newIndex !== -1) {
      const nextDisplayFields = arrayMove(displayFields, oldIndex, newIndex);

      let displayPointer = 0;
      const nextFields = fields.map((item) => {
        if (item.disableHideFunc) {
          return item;
        }
        const replacement = nextDisplayFields[displayPointer];
        displayPointer += 1;
        return replacement;
      });

      setFields(nextFields);
      if (onColumnOrderChange) {
        onColumnOrderChange(nextFields.map((item) => String(item.accessorKey)));
      }
    }
  };

  /**
   * Handle toggling freeze status for a column
   * @param {string} columnKey - The column key to freeze or unfreeze
   */
  const handleToggleFreeze = (columnKey: string) => {
    const nextFrozen = frozenColumn === columnKey ? null : columnKey;
    if (onFrozenColumnChange) {
      onFrozenColumnChange(nextFrozen);
    }
  };

  return (
    <div className="table-field-allow-warp">
      <div className="button-wrap">
        <button
          type="button"
          className="table-column-show-hide-button"
          onClick={toggleModal}
          ref={buttonReference}>
          <span className={`icon ${isAnyColumnHidden ? 'active' : ''}`}>
            <SizeChangeIcon />
          </span>
          {label && <span>{label}</span>}
        </button>
        {hoverLabel && <div className="hover-label">{hoverLabel}</div>}
      </div>
      {open &&
        ReactDOM.createPortal(
          <div className="table-field-allow-container" style={style} ref={reference}>
            <div className="table-field-allow-header">
              <div className="allow-all-field-button">
                <div
                  className="field-item"
                  onClick={() => hideAndShowFields('multi', 'hideAll')}
                  role="presentation">
                  {t('hideAll')}
                </div>
                <div
                  className="field-item"
                  onClick={() => hideAndShowFields('multi', 'showAll')}
                  role="presentation">
                  {t('showAll')}
                </div>
              </div>
            </div>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
              onDragEnd={handleDragEnd}>
              <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                <div className="allow-field-list">
                  {displayFields.map((field, index) => {
                    const isFrozen = frozenIndex !== -1 && index <= frozenIndex;
                    const isBoundary = String(field.accessorKey) === String(frozenColumn);
                    return (
                      <SortableColumnItem
                        key={String(field.accessorKey)}
                        id={String(field.accessorKey)}
                        field={field}
                        isChecked={!hidedColumn.includes(field.accessorKey)}
                        isFrozen={isFrozen}
                        isBoundary={isBoundary}
                        onToggle={(name: string) => hideAndShowFields('single', name)}
                        onToggleFreeze={handleToggleFreeze}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          </div>,
          document.body,
        )}
    </div>
  );
}

TableFieldAllow.defaultProps = {
  hoverLabel: '',
  onColumnOrderChange: undefined,
  frozenColumn: null,
  onFrozenColumnChange: undefined,
};

export default TableFieldAllow;
